import type { ComponentType } from 'react';
import { AlertCircle, Bot, Box, FolderGit2, Loader2, UserCircle } from 'lucide-react';
import type { ResourceDependent } from '@reflex/shared';
import { ResourceChip } from '../resource-preview/ResourceChip';

/** Singular/plural display nouns for one dependent `kind`. */
export interface DependentKindLabel {
  one: string;
  many: string;
}

/**
 * Display nouns for the dependent kinds every reference provider emits today
 * (host agents, personas, projects). Shared so each resource's delete dialog
 * labels them identically.
 */
export const DEFAULT_DEPENDENT_KIND_LABELS: Record<string, DependentKindLabel> = {
  agent: { one: 'active agent', many: 'active agents' },
  'agent-persona': { one: 'persona', many: 'personas' },
  project: { one: 'project', many: 'projects' },
};

/**
 * Dependent `kind` → the resource-preview registry `kind` used to resolve the
 * hover preview + detail route (the registry keys are plural).
 */
const PREVIEW_KIND: Record<string, string> = {
  agent: 'agents',
  'agent-persona': 'personas',
  project: 'projects',
};

/** Dependent `kind` → its conventional glyph, matching each entity's list page. */
const KIND_ICON: Record<string, ComponentType<{ className?: string }>> = {
  agent: Bot,
  'agent-persona': UserCircle,
  project: FolderGit2,
};

export interface DependentsWarningProps {
  /** Display name of the resource being deleted. */
  resourceName: string;
  /** Dependents returned by the resource's `.../:id/dependents` endpoint. */
  dependents: ResourceDependent[];
  /** The dependents query is in flight. */
  isLoading: boolean;
  /** The dependents query failed — must not read as "nothing depends on it". */
  isError: boolean;
  /** Map from dependent `kind` to display nouns for the summary line. */
  kindLabels: Record<string, DependentKindLabel>;
  /**
   * Verb phrase describing the impact, applied to the dependents subject —
   * e.g. "will lose access to its tools on the next launch" or "will lose its
   * egress rules on the next launch".
   */
  impact: string;
}

function Intro({ resourceName }: { resourceName: string }) {
  return (
    <p className="text-muted-foreground">
      This will permanently remove{' '}
      <span className="font-medium text-foreground">{resourceName}</span>.
    </p>
  );
}

/**
 * Body of a delete-confirm dialog that surfaces what currently depends on a
 * resource. Handles the four states — loading, error (never a false all-clear),
 * none, and a grouped, scrollable list of interactive entity chips (icon + a
 * hover preview resolved by id). Scales to many dependents via an internal
 * scroll area.
 *
 * Renders block/interactive content (`<div>`, `<button>` chips), so it must be
 * placed OUTSIDE an AlertDialogDescription (`<p>`) — use ConfirmDialog's `body`
 * slot rather than its `description`.
 */
export function DependentsWarning({
  resourceName,
  dependents,
  isLoading,
  isError,
  kindLabels,
  impact,
}: DependentsWarningProps) {
  if (isLoading) {
    return (
      <div className="space-y-3 text-sm">
        <Intro resourceName={resourceName} />
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Checking what depends on it…
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-3 text-sm">
        <Intro resourceName={resourceName} />
        <p className="flex items-start gap-2 text-muted-foreground">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>Couldn&apos;t check what depends on it. Anything using it {impact}.</span>
        </p>
      </div>
    );
  }

  if (dependents.length === 0) {
    return (
      <div className="space-y-3 text-sm">
        <Intro resourceName={resourceName} />
        <p>Nothing currently depends on it.</p>
      </div>
    );
  }

  // Group by kind, preserving first-seen order, so chips of a kind sit together
  // under a labelled, counted header.
  const groups: { kind: string; items: ResourceDependent[] }[] = [];
  for (const dep of dependents) {
    let group = groups.find((g) => g.kind === dep.kind);
    if (!group) {
      group = { kind: dep.kind, items: [] };
      groups.push(group);
    }
    group.items.push(dep);
  }

  const summary = groups
    .map(({ kind, items }) => {
      const label = kindLabels[kind] ?? { one: kind, many: `${kind}s` };
      return `${items.length} ${items.length === 1 ? label.one : label.many}`;
    })
    .join(', ');

  return (
    <div className="space-y-3 text-sm">
      <Intro resourceName={resourceName} />
      <p className="text-muted-foreground">
        {summary} {impact}:
      </p>
      <div className="max-h-56 space-y-3 overflow-y-auto rounded-md border border-border/60 bg-muted/20 p-2.5">
        {groups.map(({ kind, items }) => {
          const label = kindLabels[kind] ?? { one: kind, many: `${kind}s` };
          const Icon = KIND_ICON[kind] ?? Box;
          return (
            <div key={kind} className="space-y-1.5">
              <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <Icon className="h-3 w-3" />
                {items.length} {items.length === 1 ? label.one : label.many}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {items.map((dep) => (
                  <ResourceChip
                    key={`${dep.kind}:${dep.id}`}
                    kind={PREVIEW_KIND[dep.kind] ?? dep.kind}
                    id={dep.id}
                    name={dep.label}
                    icon={KIND_ICON[dep.kind] ?? Box}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
